// Utilities
import WindowMetrics from '../../utilities/window-metrics';

// Components
import Submenu from './submenu';

/**
 * @typedef Button
 * @prop {Element} container
 * @prop {Element} link
 * @prop {string} associatedId
 */

const settings = {
    dataAttributes: {
        associatedSection: 'data-for-section',
    },

    classNames: {
        active: 'header--active',
        disabled: 'header__section-selector--disabled',
    },

    selectors: {
        openCloseButton: ':scope > button',
        sectionButtons: 'li[data-for-section]',
    },
};

export default class SectionSelector extends Submenu {
    constructor(
        container,
        initialSectionId,
        callbacks = {},
    ) {
        // Locate our DOM elements
        const openCloseButton = container
            .querySelector(settings.selectors.openCloseButton);
        const submenu = container.querySelector('ul');

        // Call base constructor
        super(
            container,
            openCloseButton,
            submenu,
            false,
            callbacks,
        );

        this.container = container;
        this.initialSectionId = initialSectionId;
        this.isOpen = false;
        this.isDisabled = false;

        /** @type {Button} */
        this.currentSectionButton = null;

        // Locate our DOM elements
        /** @type {Button} */
        this.openCloseButton = openCloseButton;

        /** @type {Button[]} */
        this.sectionButtons = Array
            .from(
                this.container.querySelectorAll(
                    settings.selectors.sectionButtons,
                ),
            )
            .map((element) => {
                const associatedId = element
                    .getAttribute(settings.dataAttributes.associatedSection);

                if (!associatedId) {
                    throw new Error(`Section button missing '${settings.dataAttributes.associatedSection}' attribute`);
                }

                /** @type {Button} */
                return {
                    element: element,
                    associatedId: associatedId,
                };
            });

        if (
            !this.openCloseButton ||
            this.sectionButtons.length == 0
        ) {
            throw new Error('Section selector not stuctured as expected');
        }

        // Setup section buttons
        for (let i=0; i<this.sectionButtons.length; i++) {
            /** @type {Button} */
            const sectionButton = this.sectionButtons[i];

            sectionButton.element.addEventListener(
                'click',
                (event) => {
                    // The selector performs different actions based on whether
                    // we're currently in a mobile or desktop breakpoint
                    if (!WindowMetrics.isMobile()) {
                        // Desktop - allow the link to be followed normally
                        return;
                    }

                    // Mobile - perform our selection change callback,
                    // which switches the visible menu (like a tab system)
                    event.preventDefault();
                    event.stopPropagation();

                    this.setSectionId(sectionButton.associatedId);
                });
        }

        // Set initial section selection
        this.setSectionId(this.initialSectionId);
    }

    setSectionId(
        sectionId,
    ) {
        if (this.currentSectionButton) {
            // A button is currently selected
            if (sectionId == this.currentSectionButton.associatedId) {
                // Return flag indicating that no change was made
                return false;
            }
        } else {
            // A button isn't currently selected
            if (!sectionId) {
                // Return flag indicating that no change was made
                return false;
            }
        }

        // Disable the currently active button, if applicable
        if (this.currentSectionButton) {
            this.currentSectionButton
                .element
                .classList
                .remove(settings.classNames.active);

            // Clear our current selected button
            this.currentSectionButton = null;
        }

        // Activate the button associated with this section ID
        for (let i=0; i<this.sectionButtons.length; i++) {
            /** @type {Button} */
            const sectionButton = this.sectionButtons[i];

            if (sectionButton.associatedId != sectionId) {
                continue;
            }

            sectionButton
                .element
                .classList
                .add(settings.classNames.active);

            // Update our current selected button
            this.currentSectionButton = sectionButton;

            // Stop looping
            break;
        }

        // Set the copy of our open/close button
        if (this.currentSectionButton) {
            this.openCloseButton.innerText = this.currentSectionButton
                .element
                .innerText;
        } else {
            this.openCloseButton.innerText = '';
        }

        if (typeof this.callbacks.onSelectionChange == 'function') {
            this.callbacks.onSelectionChange(sectionId);
        }

        // If the section selector is open, close it
        if (this.isOpen) {
            // Doing this after a brief delay so the user
            // sees the feedback of the selection change
            // before the menu closes
            this.setDisabled(true);
            setTimeout(
                () => {
                    this.close();
                    this.setDisabled(false);
                },
                150,
            );
        }

        // Return flag indicating that a change was made
        return true;
    }

    resetSectionId() {
        this.setSectionId(this.initialSectionId);
    }

    setDisabled(disabled) {
        disabled = disabled === true;
        if (disabled) {
            if (this.isDisabled) {
                return;
            }

            this.isDisabled = true;
            this.container.classList.add(settings.classNames.disabled);
        } else {
            if (!this.isDisabled) {
                return;
            }

            this.isDisabled = false;
            this.container.classList.remove(settings.classNames.disabled);
        }
    }
}
